/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
	Avatar,
	Chip,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	useMediaQuery,
} from '@mui/material';

// third-party
import { useTranslation } from 'react-i18next';

// project imports
import config from 'config';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { openMenu, setMenuOpen } from 'config/customization';
// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }: any) => {
	const theme: any = useTheme();
	const dispatch = useDispatch();
	const location = useLocation();
	const customization = useSelector((state: any) => state.customization);
	const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
	const { t } = useTranslation();

	const Icon = item.icon;
	const itemIcon = item?.icon ? (
		<Icon stroke={1.5} size='1.3rem' />
	) : (
		<FiberManualRecordIcon
			sx={{
				width:
					customization.isOpen.findIndex((id: any) => id === item?.id) > -1
						? 8
						: 6,
				height:
					customization.isOpen.findIndex((id: any) => id === item?.id) > -1
						? 8
						: 6,
			}}
			fontSize={level > 0 ? 'inherit' : 'medium'}
		/>
	);

	let itemTarget = '_self';
	if (item.target) {
		itemTarget = '_blank';
	}

	let listItemProps: any = {
		component: forwardRef((props, ref: any) => (
			<Link
				ref={ref}
				{...props}
				// eslint-disable-next-line react/prop-types
				to={`${config.basename}${item.url}`}
				target={itemTarget}
			/>
		)),
	};
	if (item?.external) {
		listItemProps = { component: 'a', href: item.url, target: itemTarget };
	}

	const itemHandler = (id: any) => {
		dispatch(openMenu({ id }));
		if (matchesSM) dispatch(setMenuOpen({ opened: false }));
	};

	// active menu item on page load
	useEffect(() => {
		const currentIndex = location.pathname
			.toString()
			.split('/')
			.findIndex(id => id === item.id);
		if (currentIndex > -1) {
			dispatch(openMenu({ id: item.id }));
		}
		// eslint-disable-next-line
	}, [location]);

	return (
		<ListItemButton
			{...listItemProps}
			disabled={item.disabled}
			sx={{
				borderRadius: `${customization.borderRadius}px`,
				mb: 0.5,
				alignItems: 'flex-start',
				backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
				py: level > 1 ? 1 : 1.25,
				pl: `${level * 12}px`,
				textAlign: theme.direction === 'rtl' ? 'right' : 'left',
			}}
			selected={
				customization.isOpen.findIndex((id: any) => id === item.id) > -1
			}
			onClick={() => itemHandler(item.id)}
		>
			<ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36 }}>
				{itemIcon}
			</ListItemIcon>
			<ListItemText
				primary={
					<Typography
						variant={
							customization.isOpen.findIndex((id: any) => id === item.id) > -1
								? 'h5'
								: 'body1'
						}
						color='inherit'
					>
						{t(item.title)}
					</Typography>
				}
				secondary={
					item.caption && (
						<Typography
							variant='caption'
							sx={{ ...theme.typography.subMenuCaption }}
							display='block'
							gutterBottom
						>
							{item.caption}
						</Typography>
					)
				}
			/>
			{item.chip && (
				<Chip
					color={item.chip.color}
					variant={item.chip.variant}
					size={item.chip.size}
					label={item.chip.label}
					avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
				/>
			)}
		</ListItemButton>
	);
};

export default NavItem;
